import { formatPhoneOrDigits } from "../fields/PhoneNumberInput";
import { VERIFICATION_OUTCOME } from "./callScript.savePayload";
import type { ResidenceQuestionFieldKey, ResidenceVerifiedFields } from "./callScriptResidence.types";
import type { ResidenceQuestionPkMap } from "./callScriptResidenceQuestions";
import { mergeOptionalNumber, mergeOptionalString, toYesNo } from "./callScriptShared.payload";
import type { CallScriptApplicantRole, CallScriptSaveWarning } from "./callScriptShared.types";

// ---------------------------------------------------------------------------
// callScriptResidence.savePayload
//
// Builds the POST body for the residence ("Customer Verification") Call
// Script save. Mirrors callScript.savePayload.ts (the employment equivalent):
// a non-blank "Verified Information" value wins, otherwise the saved value is
// sent, so an unrelated field the analyst never touched isn't blanked out
// server-side; a field with neither is left `undefined` and omitted from the
// JSON entirely.
//
// PROVISIONAL CONTRACT: saveResidenceCallScript types its body as `object`
// (see callScriptResidence.types.ts), so the field names below follow the
// employment body's conventions plus CAPS2's own naming. `verificationAnswers[]`
// matches the employment contract exactly. Reconcile the rest against recap.
// ---------------------------------------------------------------------------

/** One question's verified-vs-stored answer comparison — same shape as the employment script's. */
export interface ResidenceCallScriptVerificationAnswer {
  /** Resolved from the `getResidenceVerificationQuestions` catalog — see buildResidenceQuestionPkMap. */
  verificationQuestionPk: number;
  answerEntered: string;
  answerStored: string;
  isAnswerMatch: "Y" | "N";
}

export type ResidenceCallScriptApplicantRole = CallScriptApplicantRole;
export type ResidenceCallScriptSaveWarning = CallScriptSaveWarning;

/** Request body for the residence Call Script save (POST) endpoint. */
export interface ResidenceCallScriptSaveBody {
  applicantRole: CallScriptApplicantRole;
  applicantAddressPk?: number;
  /** Contact block. */
  phoneNumberDialed: string;
  /** One of {@link VERIFICATION_OUTCOME} — see computeResidenceVerificationOutcome. Required by recap. */
  verificationOutcome: string;
  /** Always "N" — CAPS2 hard-codes it for the residence script. Required by recap. */
  isVerificationOutsourced: "Y" | "N";
  isPhoneNoBad: "Y" | "N";
  dateOfBirth?: string;
  primaryNumber?: string;
  secondaryNumber?: string;
  /** The side panel's "does not want to be contacted at Primary Number" checkbox. */
  isDoNotContactPrimary: "Y" | "N";
  isEContractConsent?: "Y" | "N";
  isSignedCopyProvided?: "Y" | "N";
  /** Physical address. */
  physicalStreetNumber?: string;
  physicalStreetName?: string;
  physicalApartmentNumber?: string;
  physicalCity?: string;
  physicalState?: string;
  physicalZip?: string;
  /** Mailing address. */
  mailingStreetNumber?: string;
  mailingStreetName?: string;
  mailingApartmentNumber?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZip?: string;
  /** Deal / down payment block. */
  whoIsPrimaryDriver?: string;
  isTradeIn?: "Y" | "N";
  totalCashAndTradeInPaidOnDelivery?: number;
  isAnyAmountStillOwed?: "Y" | "N";
  remainingAmountDueDate?: string;
  grossTradeInAmount?: number;
  tradeInPayoffAmount?: number;
  cashDownPaidOnDelivery?: number;
  amountDueAfterDelivery?: number;
  finalAmountDueDate?: string;
  noteText: string;
  verificationAnswers: ResidenceCallScriptVerificationAnswer[];
}

/** Response from the residence Call Script save — persisted identifiers only, no full entry. */
export interface ResidenceCallScriptSaveResponse {
  verificationPhoneCallPk: number;
  verificationAnswerPks: number[];
  dpscNotePk?: number;
  occurredAt: string;
  warnings?: CallScriptSaveWarning[];
}

/** Everything the payload builder needs beyond the ids in the URL path. */
export interface ResidenceCallScriptSaveContext {
  personPath: "driver" | "secondSigner";
  applicantAddressPk?: number;
  note: string;
  doNotContactPrimary: boolean;
  verifiedFields: ResidenceVerifiedFields;
  /** The "Saved Information" column — the fallback for any field left blank on the call. */
  savedFields: ResidenceVerifiedFields;
  /** Field -> verificationQuestionPk, resolved from the fetched question catalog. */
  questionPkMap: ResidenceQuestionPkMap;
  /** The applicant's most recent saved call's outcome, if any — an earlier RPC carries forward. */
  previousVerificationOutcome?: string;
}

/**
 * Derives the residence call's outcome, porting CAPS2's
 * Step6ApplicantResidenceVerificationBackingBean.setValuesToVerificationPhoneCall:
 *   - once the applicant's previous call was RPC, every later call stays RPC;
 *   - otherwise no number dialed means no attempt was made;
 *   - a verified date of birth means the right party was reached;
 *   - absent one, a confirmed-bad original number means the lead isn't
 *     fundable, otherwise no RPC but no issues found.
 * The employment script's `computeVerificationOutcome` is the same shape,
 * keyed on hire date instead of date of birth and without the RPC carry-forward.
 * @param {ResidenceVerifiedFields} verified - The "Verified Information" values.
 * @param {string} [previousOutcome] - The most recent saved call's outcome.
 * @returns {string} One of the {@link VERIFICATION_OUTCOME} values.
 */
export function computeResidenceVerificationOutcome(
  verified: ResidenceVerifiedFields,
  previousOutcome?: string
): string {
  if (previousOutcome?.toLowerCase() === VERIFICATION_OUTCOME.RPC.toLowerCase()) {
    return VERIFICATION_OUTCOME.RPC;
  }
  if (!verified.phoneNumberDialed.replaceAll(/\D/gu, "")) {
    return VERIFICATION_OUTCOME.NO_ATTEMPT;
  }
  if (verified.dateOfBirth.trim()) {
    return VERIFICATION_OUTCOME.RPC;
  }
  return verified.wasOriginalNumberBad ? VERIFICATION_OUTCOME.NOT_FUNDABLE : VERIFICATION_OUTCOME.NO_RPC_NO_ISSUES;
}

/**
 * Maps a `"" | "Y" | "N"` tri-state onto the wire's Y/N, omitting it entirely
 * while still unanswered.
 * @param {string} value - The tri-state value.
 * @returns {"Y" | "N" | undefined} The flag to send, or undefined to omit it.
 */
function toOptionalFlag(value: string): "Y" | "N" | undefined {
  if (value === "Y" || value === "N") {
    return value;
  }
  return undefined;
}

/** Phone-valued question fields, formatted xxx-xxx-xxxx on both sides of the answer comparison. */
const PHONE_ANSWER_FIELDS = new Set<ResidenceQuestionFieldKey>([
  "phoneNumberDialed",
  "primaryNumber",
  "secondaryNumber",
]);

type ResidenceQuestionFieldValue = ResidenceVerifiedFields[ResidenceQuestionFieldKey];

/**
 * Whether a field value counts as unanswered. Booleans are never blank.
 * @param {ResidenceQuestionFieldValue} value - The raw field value.
 * @returns {boolean} Whether the value is blank.
 */
function isBlank(value: ResidenceQuestionFieldValue): boolean {
  return typeof value !== "boolean" && String(value).trim() === "";
}

/**
 * Renders a field's value the way the save contract's `answerEntered` /
 * `answerStored` carry it: Y/N for booleans, xxx-xxx-xxxx for phones, the
 * raw string or number otherwise.
 * @param {ResidenceQuestionFieldKey} field - Which field the value belongs to.
 * @param {ResidenceQuestionFieldValue} value - The field's value.
 * @returns {string} The wire representation.
 */
function toAnswerText(field: ResidenceQuestionFieldKey, value: ResidenceQuestionFieldValue): string {
  if (typeof value === "boolean") {
    return toYesNo(value);
  }
  const text = String(value);
  return PHONE_ANSWER_FIELDS.has(field) ? formatPhoneOrDigits(text) : text;
}

/**
 * Builds the per-question `verificationAnswers[]`. Mirrors the employment
 * script's `buildVerificationAnswers`: one row per field that both resolved a
 * `verificationQuestionPk` from the catalog AND was answered on the call;
 * `isAnswerMatch` is "Y" only when there is a stored value and it matches
 * the entered one case-insensitively.
 * @param {ResidenceVerifiedFields} verified - The "Verified Information" values.
 * @param {ResidenceVerifiedFields} saved - The "Saved Information" values.
 * @param {ResidenceQuestionPkMap} pkMap - Field -> verificationQuestionPk.
 * @returns {ResidenceCallScriptVerificationAnswer[]} The answer rows for the save request.
 */
export function buildResidenceVerificationAnswers(
  verified: ResidenceVerifiedFields,
  saved: ResidenceVerifiedFields,
  pkMap: ResidenceQuestionPkMap
): ResidenceCallScriptVerificationAnswer[] {
  const answers: ResidenceCallScriptVerificationAnswer[] = [];
  for (const field of Object.keys(pkMap) as ResidenceQuestionFieldKey[]) {
    const verificationQuestionPk = pkMap[field];
    const enteredValue = verified[field];
    if (verificationQuestionPk === undefined || isBlank(enteredValue)) {
      continue;
    }
    const answerEntered = toAnswerText(field, enteredValue);
    const storedValue = saved[field];
    const answerStored = isBlank(storedValue) ? "" : toAnswerText(field, storedValue);
    const isAnswerMatch = answerStored !== "" && answerEntered.toLowerCase() === answerStored.toLowerCase();
    answers.push({ verificationQuestionPk, answerEntered, answerStored, isAnswerMatch: toYesNo(isAnswerMatch) });
  }
  return answers;
}

/**
 * Builds the residence Call Script save body from the modal's live state.
 * @param {ResidenceCallScriptSaveContext} context - The modal's captured values plus the ids it needs.
 * @returns {ResidenceCallScriptSaveBody} The POST body.
 */
export function buildResidenceCallScriptSaveBody(context: ResidenceCallScriptSaveContext): ResidenceCallScriptSaveBody {
  const { verifiedFields: verified, savedFields: saved } = context;
  return {
    applicantRole: context.personPath === "driver" ? "DRIVER" : "SECOND_SIGNER",
    applicantAddressPk: context.applicantAddressPk,
    phoneNumberDialed: verified.phoneNumberDialed.replaceAll(/\D/gu, ""),
    verificationOutcome: computeResidenceVerificationOutcome(verified, context.previousVerificationOutcome),
    isVerificationOutsourced: "N",
    isPhoneNoBad: toYesNo(verified.wasOriginalNumberBad),
    dateOfBirth: mergeOptionalString(verified.dateOfBirth, saved.dateOfBirth),
    primaryNumber: mergeOptionalString(verified.primaryNumber, saved.primaryNumber),
    secondaryNumber: mergeOptionalString(verified.secondaryNumber, saved.secondaryNumber),
    isDoNotContactPrimary: toYesNo(context.doNotContactPrimary),
    isEContractConsent: toOptionalFlag(verified.consentToSignElectronically),
    isSignedCopyProvided: toOptionalFlag(verified.signedCopyProvided),
    physicalStreetNumber: mergeOptionalString(verified.physicalStreetNumber, saved.physicalStreetNumber),
    physicalStreetName: mergeOptionalString(verified.physicalStreetName, saved.physicalStreetName),
    physicalApartmentNumber: mergeOptionalString(verified.physicalApartmentNumber, saved.physicalApartmentNumber),
    physicalCity: mergeOptionalString(verified.physicalCity, saved.physicalCity),
    physicalState: mergeOptionalString(verified.physicalState, saved.physicalState),
    physicalZip: mergeOptionalString(verified.physicalZip, saved.physicalZip),
    mailingStreetNumber: mergeOptionalString(verified.mailingStreetNumber, saved.mailingStreetNumber),
    mailingStreetName: mergeOptionalString(verified.mailingStreetName, saved.mailingStreetName),
    mailingApartmentNumber: mergeOptionalString(verified.mailingApartmentNumber, saved.mailingApartmentNumber),
    mailingCity: mergeOptionalString(verified.mailingCity, saved.mailingCity),
    mailingState: mergeOptionalString(verified.mailingState, saved.mailingState),
    mailingZip: mergeOptionalString(verified.mailingZip, saved.mailingZip),
    whoIsPrimaryDriver: mergeOptionalString(verified.whoIsPrimaryDriver, saved.whoIsPrimaryDriver),
    isTradeIn: toOptionalFlag(verified.tradeIn),
    totalCashAndTradeInPaidOnDelivery: mergeOptionalNumber(verified.totalCashAndTradeIn, saved.totalCashAndTradeIn),
    isAnyAmountStillOwed: toOptionalFlag(verified.anyAmountStillOwed),
    remainingAmountDueDate: mergeOptionalString(verified.whenIsRemainingDue, saved.whenIsRemainingDue),
    grossTradeInAmount: mergeOptionalNumber(verified.grossTradeInAmount, saved.grossTradeInAmount),
    tradeInPayoffAmount: mergeOptionalNumber(verified.tradeInPayoffAmount, saved.tradeInPayoffAmount),
    cashDownPaidOnDelivery: mergeOptionalNumber(verified.cashDownPaidOnDelivery, saved.cashDownPaidOnDelivery),
    amountDueAfterDelivery: mergeOptionalNumber(verified.amountDueAfterDelivery, saved.amountDueAfterDelivery),
    finalAmountDueDate: mergeOptionalString(verified.whenIsFinalAmountDue, saved.whenIsFinalAmountDue),
    noteText: context.note,
    verificationAnswers: buildResidenceVerificationAnswers(verified, saved, context.questionPkMap),
  };
}
